import type { CategoryType } from '@/Utility/categories';
import { updateCategoryMutation } from '@/graphql/mutations/Category.mutation';
import {
  CategoriesFragment,
  type Category_Fragment$data,
  type Category_Fragment$key,
} from '@/graphql/queries/Category.query';
import type { ZnglyContext_TopBarNavigation_Query$data } from '@/graphql/queries/ZnglyContext.query';
import { useRelayStore } from '@/graphql/useRelayStore';
import { useEffect, useMemo } from 'react';
import { useFragment } from 'react-relay';
import { useUser } from '../useUser';

// type Node = Category_Fragment$data['categories']['edges'][number];
type Node = NonNullable<Category_Fragment$data['categories']>['edges'][number];

// recursively transform the category data into a tree
const categoryMap = ({ node }: Node, parent: CategoryType | null = null): CategoryType => {
  const { children, znglycategory, ...top } = node;

  const base = {
    ...top,
    order: znglycategory?.uxOrder || 0,
    content: znglycategory?.pinnedContent ? [...znglycategory.pinnedContent] : null,
  } as CategoryType;

  let updateChildren = {
    __id: '',
    edges: [] as CategoryType[],
  };

  if (children)
    updateChildren = {
      __id: children.__id,
      edges: formatCategoryOrder(
        children?.edges
          .filter(({ node }) => !!node)
          .map((child) => {
            return categoryMap(child as Node, base);
          })
      ),
    };

  return {
    ...base,
    // @ts-ignore
    children: updateChildren,
    // @ts-ignore
    parent,
  };
};

const formatCategoryOrder = (categories: CategoryType[] = [], canUpdate = false): CategoryType[] => {
  // sort the categories by order, if order is not set, then set it to the index
  // the order should be self healing if the order is not set

  categories.forEach((category, index) => {
    // get the list of orders, filter and sorted
    const originalOrder = { ...category }.order || 0;

    const orders = categories
      .map((c) => c.order)
      .filter((order) => order !== null || order !== -1)
      .sort((a, b) => a - b);

    if (!category.order || category.order === -1) {
      // loop through the orders and find the first one that is not the current index
      for (let i = 0; i < orders.length; i++)
        if (orders[i] > index) {
          category.order = i;
          break;
        }

      // if the order is not set, set it to the highest order + 1
      category.order = Math.max(...orders) === Number.NEGATIVE_INFINITY ? 0 : Math.max(...orders) + 1;
    }

    // fix the order if it is not correct
    if (category.order !== originalOrder && canUpdate)
      updateCategoryMutation({
        input: {
          id: category.id,
          uxOrder: category.order,
          name: category.name,
        },
      });

    return {
      ...category,
      order: category.order,
    };
  });

  return categories.sort((a, b) => a.order - b.order);
};

export type CategoriesType = {
  __id?: string;
  edges: CategoryType[];
};

const useCategorySetter = (query: ZnglyContext_TopBarNavigation_Query$data): CategoriesType => {
  const data = useFragment<Category_Fragment$key>(CategoriesFragment, query);

  const { isAdmin } = useUser();

  // memo the result of the categoryMap function
  const categories = useMemo(() => {
    const filtered = data?.categories?.edges.filter(({ node }) => !!node).map((node) => categoryMap(node as Node));

    return formatCategoryOrder(filtered, isAdmin);
  }, [JSON.stringify(data), isAdmin]);

  useEffect(() => {
    if (typeof data?.categories?.__id === 'string')
      useRelayStore.getState().set__id('Category_Fragment', data.categories.__id);
  }, [data?.categories?.__id]);

  if (!categories || !categories?.length) {
    return {
      __id: data?.categories?.__id,
      edges: [],
    };
  }

  return {
    __id: data?.categories?.__id,
    edges: categories,
  };
};

export default useCategorySetter;
